import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      import('aws-amplify/auth').then(({ signOut }) => signOut()).catch(() => {});
    }

    return Promise.reject(error);
  }
);

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  message?: string;
}

// Generic API request function
async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.request<ApiResponse<T>>({
      method,
      url: endpoint,
      data,
      ...config,
    });

    if (response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }

    throw new Error(response.data.error?.message || 'Unknown error occurred');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiResponse<never>;
      throw new Error(apiError?.error?.message || error.message);
    }
    throw error;
  }
}

// User API
export const userApi = {
  list: (params?: { limit?: number; paginationToken?: string; filter?: string }) =>
    apiRequest<{ users: any[]; paginationToken?: string }>('GET', '/users', undefined, { params }),

  get: (username: string) => apiRequest<any>('GET', `/users/${username}`),

  create: (data: any) => apiRequest<any>('POST', '/users', data),

  update: (username: string, data: any) =>
    apiRequest<any>('PUT', `/users/${username}`, data),

  delete: (username: string) => apiRequest<{ deleted: boolean }>('DELETE', `/users/${username}`),

  enable: (username: string) =>
    apiRequest<{ enabled: boolean }>('POST', `/users/${username}/enable`),

  disable: (username: string) =>
    apiRequest<{ disabled: boolean }>('POST', `/users/${username}/disable`),

  resetPassword: (username: string) =>
    apiRequest<{ temporaryPassword: string }>('POST', `/users/${username}/reset-password`),

  addToGroup: (username: string, groupName: string) =>
    apiRequest<{ added: boolean }>('POST', `/users/${username}/groups`, { groupName }),

  removeFromGroup: (username: string, groupName: string) =>
    apiRequest<{ removed: boolean }>('DELETE', `/users/${username}/groups/${groupName}`),
};

// Group API
export const groupApi = {
  list: () => apiRequest<any[]>('GET', '/groups'),

  listMembers: (groupName: string) =>
    apiRequest<{ username: string; email: string }[]>('GET', `/groups/${encodeURIComponent(groupName)}/members`),

  getHistory: (groupName: string) =>
    apiRequest<any[]>('GET', `/groups/${encodeURIComponent(groupName)}/history`),

  listDeleted: () => apiRequest<any[]>('GET', '/groups/deleted'),

  getDeleted: (groupName: string) =>
    apiRequest<any>('GET', `/groups/deleted/${encodeURIComponent(groupName)}`),

  create: (data: { groupName: string; description?: string; precedence?: number }) =>
    apiRequest<any>('POST', '/groups', data),

  update: (groupName: string, data: { description?: string; precedence?: number }) =>
    apiRequest<any>('PUT', `/groups/${encodeURIComponent(groupName)}`, data),

  delete: (groupName: string) =>
    apiRequest<{ deleted: boolean }>('DELETE', `/groups/${encodeURIComponent(groupName)}`),
};

export type AccessLevelInput =
  | { levelName: string; type: 'group'; cognitoGroupName: string }
  | { levelName: string; type: 'attribute'; attributeKey: string; attributeValue: string };

// App definitions API
export const appApi = {
  list: () => apiRequest<any[]>('GET', '/apps'),

  create: (data: { name: string; accessLevels: AccessLevelInput[] }) =>
    apiRequest<any>('POST', '/apps', data),

  update: (appId: string, data: { name?: string; accessLevels?: AccessLevelInput[] }) =>
    apiRequest<any>('PUT', `/apps/${appId}`, data),

  delete: (appId: string) => apiRequest<{ deleted: boolean }>('DELETE', `/apps/${appId}`),
};

// Attributes API
export const attributesApi = {
  get: () => apiRequest<{
    users: {
      username: string;
      email: string;
      givenName: string;
      familyName: string;
      customAttributes: Record<string, string>;
    }[];
    attributeKeys: string[];
  }>('GET', '/attributes'),
};

// Matrix API
export const matrixApi = {
  get: () => apiRequest<{
    columns: {
      appId: string;
      appName: string;
      levelName: string;
      type: 'group' | 'attribute';
      cognitoGroupName?: string;
      attributeKey?: string;
      attributeValue?: string;
    }[];
    rows: {
      userId: string;
      username: string;
      email: string;
      givenName: string;
      familyName: string;
      groups: string[];
      customAttributes: Record<string, string>;
    }[];
  }>('GET', '/matrix'),
};

// Profile API (self-service)
export const profileApi = {
  get: () => apiRequest<any>('GET', '/me'),

  update: (data: { givenName?: string; familyName?: string }) =>
    apiRequest<any>('PUT', '/me', data),
};

// Health check
export const healthCheck = () => apiRequest<{ status: string; timestamp: string }>('GET', '/health');

export default api;
